'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from '@phosphor-icons/react';
import { cn } from '../../utils/cn';

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxWidth?: string;
}

export function Dialog({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'max-w-md',
}: DialogProps) {
  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <DialogPrimitive.Overlay asChild>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm"
                />
              </DialogPrimitive.Overlay>

              {/* Dialog Content */}
              <DialogPrimitive.Content asChild>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                  className={cn(
                    'fixed inset-0 z-50 flex items-center justify-center p-4',
                    'pointer-events-none'
                  )}
                >
                  <div
                    className={cn(
                      'relative w-full max-h-[90vh] overflow-y-auto',
                      'rounded-2xl bg-white p-6 shadow-2xl shadow-zinc-900/20',
                      'pointer-events-auto',
                      maxWidth
                    )}
                  >
                  {/* Close Button */}
                  <DialogPrimitive.Close asChild>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="absolute right-4 top-4 rounded-full p-2 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
                    >
                      <X className="h-5 w-5" />
                    </motion.button>
                  </DialogPrimitive.Close>

                  {/* Title */}
                  {title && (
                    <DialogPrimitive.Title className="text-lg font-semibold text-zinc-900 mb-4">
                      {title}
                    </DialogPrimitive.Title>
                  )}

                  {/* Content */}
                  {children}
                  </div>
                </motion.div>
              </DialogPrimitive.Content>
            </>
          )}
        </AnimatePresence>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
